/**
 * AURA Text Magnifier
 * Shows enlarged text near the pointer using the browser's caret API
 * for precise text targeting, with fallback to element-based extraction.
 * Supports both mouse and touch input for desktop and mobile.
 */

import { Logger } from '../../core/logger';
import {
  addPointerMoveListeners,
  PointerCoords,
} from '../../core/pointer-utils';

const logger = new Logger('TextMagnifier');
const MAX_TEXT_LENGTH = 300;

let magnifierElement: HTMLElement | null = null;
let magnifierEnabled = false;
let removeListeners: (() => void) | null = null;

export function enableTextMagnifier(): void {
  if (magnifierEnabled) return;

  const appendTarget = document.body ?? document.documentElement;
  if (!appendTarget) {
    logger.warn('No DOM target available for magnifier');
    return;
  }

  magnifierElement = document.createElement('div');
  magnifierElement.setAttribute('data-aura', 'magnifier');
  magnifierElement.style.cssText = `
    position: fixed; background: #fff; color: #212529;
    border: 2px solid #1e3a5f; border-radius: 8px;
    padding: 12px 16px; font-size: 24px; line-height: 1.4;
    max-width: 400px; max-height: 200px; overflow: hidden;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    z-index: 999999; pointer-events: none;
    opacity: 0; transition: opacity 0.15s ease;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  `;

  appendTarget.appendChild(magnifierElement);
  removeListeners = addPointerMoveListeners(
    document,
    handleMagnifierPointerMove,
  );
  magnifierEnabled = true;
  logger.debug('Text magnifier enabled');
}

export function disableTextMagnifier(): void {
  if (!magnifierEnabled) return;

  removeListeners?.();
  removeListeners = null;
  magnifierElement?.remove();
  magnifierElement = null;
  magnifierEnabled = false;
  logger.debug('Text magnifier disabled');
}

/**
 * Resolve the visible text at the given viewport position using the
 * browser's caret API, then fall back to the innermost text element.
 */
export function getTextAtPoint(x: number, y: number): string | null {
  return getTextFromCaretApi(x, y) ?? getTextFromTarget(x, y);
}

function getTextFromCaretApi(x: number, y: number): string | null {
  let textNode: Node | null = null;

  try {
    if ('caretRangeFromPoint' in document) {
      const range = document.caretRangeFromPoint(x, y);
      if (range) textNode = range.startContainer;
    } else if ('caretPositionFromPoint' in document) {
      const pos = (document as any).caretPositionFromPoint(x, y);
      if (pos) textNode = pos.offsetNode;
    }
  } catch (_) {
    return null;
  }

  if (!textNode || textNode.nodeType !== Node.TEXT_NODE) return null;

  const parent = textNode.parentElement;
  if (!parent || parent.closest('[data-aura]')) return null;

  const text = parent.innerText?.trim();
  if (!text) return null;

  return truncateText(text);
}

function getTextFromTarget(x: number, y: number): string | null {
  const target = document.elementFromPoint(x, y) as HTMLElement | null;
  if (!target || target.closest('[data-aura]')) return null;

  const leaf = findInnermostTextElement(target);
  const text = leaf.innerText?.trim();
  if (!text) return null;

  return truncateText(text);
}

function findInnermostTextElement(el: HTMLElement): HTMLElement {
  for (const child of Array.from(el.children)) {
    if (!(child instanceof HTMLElement)) continue;
    if (child.children.length === 0 && child.innerText?.trim()) return child;
    const deeper = findInnermostTextElement(child);
    if (deeper !== child) return deeper;
    if (child.innerText?.trim()) return child;
  }
  return el;
}

function truncateText(text: string): string | null {
  if (!text || text.length === 0) return null;
  if (text.length <= MAX_TEXT_LENGTH) return text;
  return `${text.substring(0, MAX_TEXT_LENGTH - 3)}...`;
}

function handleMagnifierPointerMove(coords: PointerCoords): void {
  if (!magnifierElement) return;

  const text = getTextAtPoint(coords.clientX, coords.clientY);

  if (text) {
    magnifierElement.textContent = text;
    magnifierElement.style.opacity = '1';

    const x = Math.min(coords.clientX + 20, window.innerWidth - 420);
    const y = Math.min(coords.clientY + 20, window.innerHeight - 100);
    magnifierElement.style.left = `${x}px`;
    magnifierElement.style.top = `${y}px`;
  } else {
    magnifierElement.style.opacity = '0';
  }
}
